import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { ModelName } from '@casops/store/api';
import { useAppDispatch } from '@casops/store/hooks';
import { openSidePanel, SidePanel } from '@casops/store/slices/globalSlice';
import { VdsButton, VdsIcon } from '@volt/volt-extended-components-react';
import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface EditItemButtonProps {
  sidePanel: SidePanel;
  modelName: ModelName;
  modelId?: number; // Allow undefined to preserve button layout.
  permission: Permission;
  disabled?: boolean;
  icon?: string;
  label?: string;
}

export const EditItemButton = ({
  sidePanel,
  modelName,
  modelId,
  permission,
  disabled = false,
  icon = 'edit',
  label,
}: EditItemButtonProps): ReactElement | null => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isShow = useIsUserWithPermission(permission);

  const onClick = useCallback(() => {
    if (typeof modelId !== 'undefined') {
      dispatch(openSidePanel({ sidePanel, modelId: modelId.toString(), editMode: true }));
    }
  }, [dispatch, sidePanel, modelId]);

  return isShow ? (
    <VdsButton
      variant="secondary"
      size="medium"
      isFullWidth={false}
      onClick={onClick}
      // Disable the button while waiting for the model data.
      disabled={disabled || typeof modelId === 'undefined'}
    >
      {label ?? t('Model.Edit.Title', { modelName })}
      <VdsIcon slot="icon-left" svgIcon={icon} />
    </VdsButton>
  ) : null;
};

export default EditItemButton;
